import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useReviewDialog } from '../../hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { 
  Truck, Target, Zap, Key, Fuel, Anchor, Battery, Wrench,
  MapPin, Clock, Star, ChevronRight, Search, Calendar,
  CheckCircle, XCircle, Loader2, Car, DollarSign, MessageSquare,
  Plus, Filter, ArrowUpDown, Download, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { ReviewDialog, StarRating } from './components/ReviewComponents';
import NotificationPreferences from './components/NotificationPreferences';

// Service icons mapping
const SERVICE_ICONS = {
  towing: Truck,
  tire_change: Target,
  jump_start: Zap,
  lockout: Key,
  fuel_delivery: Fuel,
  winch_out: Anchor,
  battery_replace: Battery,
  mobile_mechanic: Wrench,
};

// Status configurations
const STATUS_CONFIG = {
  awaiting_payment: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'Awaiting Payment', icon: DollarSign },
  pending: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'Finding Provider', icon: Search },
  awaiting_provider_payment: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Provider Confirming', icon: Clock },
  accepted: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Accepted', icon: CheckCircle },
  en_route: { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', label: 'En Route', icon: Truck },
  arrived: { color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', label: 'Arrived', icon: MapPin },
  in_progress: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'In Progress', icon: Wrench },
  completed: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Completed', icon: CheckCircle },
  cancelled: { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Cancelled', icon: XCircle },
  expired: { color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', label: 'Expired', icon: Clock },
};

export default function RoadsideHistoryPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  
  // Use the review dialog hook
  const { 
    openReviewDialog: handleLeaveReview, 
    reviewDialogProps 
  } = useReviewDialog({
    onReviewSubmitted: () => fetchRequests() // Refresh requests after review
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    filterAndSortRequests();
  }, [requests, activeTab, searchQuery, sortOrder]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/roadside/requests');
      setRequests(res.data || []);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      toast.error('Failed to load request history');
    }
    setLoading(false);
  };

  const downloadReceipt = async (requestId, e) => {
    e.stopPropagation();
    try {
      const response = await api.get(`/roadside/requests/${requestId}/receipt/pdf`, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `drayly_receipt_${requestId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Receipt downloaded');
    } catch (error) {
      console.error('Failed to download receipt:', error);
      toast.error('Failed to download receipt');
    }
  };

  const filterAndSortRequests = () => {
    let filtered = [...requests];
    
    // Filter by tab
    if (activeTab === 'active') {
      filtered = filtered.filter(r => 
        ['pending', 'awaiting_payment', 'awaiting_provider_payment', 'accepted', 'en_route', 'arrived', 'in_progress'].includes(r.status)
      );
    } else if (activeTab === 'completed') {
      filtered = filtered.filter(r => r.status === 'completed');
    } else if (activeTab === 'cancelled') {
      filtered = filtered.filter(r => ['cancelled', 'expired'].includes(r.status));
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.service_name?.toLowerCase().includes(query) ||
        r.provider_name?.toLowerCase().includes(query) ||
        r.location?.city?.toLowerCase().includes(query) ||
        r.location?.address?.toLowerCase().includes(query) ||
        r.id?.toLowerCase().includes(query)
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    
    setFilteredRequests(filtered);
  };

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleViewRequest = (request) => {
    // Navigate to tracking page for active requests, or a detail view for completed
    if (['completed', 'cancelled', 'expired'].includes(request.status)) {
      // Could navigate to a detail page, for now show toast
      toast.info('Request details coming soon');
    } else {
      navigate(`/roadside/track/${request.id}`);
    }
  };

  const activeCount = requests.filter(r => 
    ['pending', 'awaiting_payment', 'awaiting_provider_payment', 'accepted', 'en_route', 'arrived', 'in_progress'].includes(r.status)
  ).length;

  const completedCount = requests.filter(r => r.status === 'completed').length;
  const cancelledCount = requests.filter(r => ['cancelled', 'expired'].includes(r.status)).length;

  // Request Card Component
  const RequestCard = ({ request }) => {
    const statusConfig = getStatusConfig(request.status);
    const ServiceIcon = SERVICE_ICONS[request.service_type] || Wrench;
    const StatusIcon = statusConfig.icon;
    const isActive = !['completed', 'cancelled', 'expired'].includes(request.status);
    
    return (
      <Card 
        className={`cursor-pointer transition-all hover:border-blue-500/50 ${isActive ? 'border-l-4 border-l-blue-500' : ''}`}
        onClick={() => handleViewRequest(request)}
        data-testid={`request-card-${request.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            {/* Left: Service Info */}
            <div className="flex items-start gap-3 flex-1">
              <div className={`p-2.5 rounded-lg ${isActive ? 'bg-blue-500/10' : 'bg-slate-800'}`}>
                <ServiceIcon className={`h-5 w-5 ${isActive ? 'text-blue-400' : 'text-slate-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold truncate">{request.service_name}</h3>
                  <Badge className={statusConfig.color}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                </div>
                
                {/* Location */}
                <div className="flex items-center gap-1 text-sm text-slate-400 mb-1">
                  <MapPin className="h-3.5 w-3.5" />
                  <span className="truncate">
                    {request.location?.address || request.location?.city}, {request.location?.state}
                  </span>
                </div>
                
                {/* Vehicle */}
                {request.vehicle && (
                  <div className="flex items-center gap-1 text-sm text-slate-400">
                    <Car className="h-3.5 w-3.5" />
                    <span>
                      {request.vehicle.year} {request.vehicle.make} {request.vehicle.model}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right: Date & Action */}
            <div className="text-right flex flex-col items-end gap-2">
              <div className="text-xs text-slate-500">
                <Calendar className="h-3 w-3 inline mr-1" />
                {formatDate(request.created_at)}
              </div>
              
              {/* Provider info for accepted requests */}
              {request.provider_name && (
                <div className="text-xs text-slate-400">
                  <Truck className="h-3 w-3 inline mr-1" />
                  {request.provider_name}
                </div>
              )}
              
              {/* Fee paid indicator */}
              {request.customer_paid && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-400">
                    <DollarSign className="h-3 w-3 inline" />
                    ${request.platform_fee?.toFixed(2)} paid
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                    onClick={(e) => downloadReceipt(request.id, e)}
                    title="Download Receipt"
                    data-testid={`download-receipt-${request.id}`}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
              
              <ChevronRight className="h-4 w-4 text-slate-500" />
            </div>
          </div>
          
          {/* Active request progress indicator */}
          {isActive && request.status !== 'awaiting_payment' && (
            <div className="mt-3 pt-3 border-t border-slate-800">
              <div className="flex items-center gap-2">
                {['pending', 'accepted', 'en_route', 'arrived', 'in_progress'].map((step, idx) => {
                  const stepOrder = ['pending', 'accepted', 'en_route', 'arrived', 'in_progress'];
                  const currentIdx = stepOrder.indexOf(request.status);
                  const stepIdx = stepOrder.indexOf(step);
                  const isComplete = stepIdx <= currentIdx;
                  const isCurrent = step === request.status;
                  
                  return (
                    <React.Fragment key={step}>
                      <div className={`h-2 w-2 rounded-full ${
                        isComplete ? 'bg-blue-500' : 'bg-slate-700'
                      } ${isCurrent ? 'ring-2 ring-blue-500/30' : ''}`} />
                      {idx < 4 && (
                        <div className={`flex-1 h-0.5 ${
                          stepIdx < currentIdx ? 'bg-blue-500' : 'bg-slate-700'
                        }`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Review prompt for completed requests */}
          {request.status === 'completed' && request.provider_id && !request.has_review && (
            <div className="mt-3 pt-3 border-t border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-amber-400">
                <Star className="h-4 w-4" />
                <span>Rate your experience</span>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                onClick={(e) => handleLeaveReview(request, e)}
                data-testid={`review-btn-${request.id}`}
              >
                Leave Review
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-6" data-testid="roadside-history-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Request History</h1>
            <p className="text-slate-400 text-sm">View your roadside assistance requests</p>
          </div>
          <Button onClick={() => navigate('/roadside')} data-testid="new-request-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Button>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="bg-slate-900/50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-400">{activeCount}</p>
              <p className="text-xs text-slate-400">Active</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-400">{completedCount}</p>
              <p className="text-xs text-slate-400">Completed</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-400">{cancelledCount}</p>
              <p className="text-xs text-slate-400">Cancelled</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Search & Filter */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by service, provider, location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
            data-testid="sort-btn"
          >
            <ArrowUpDown className="h-4 w-4 mr-1" />
            {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
          </Button>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="all">
              All ({requests.length})
            </TabsTrigger>
            <TabsTrigger value="active">
              Active ({activeCount})
            </TabsTrigger>
            <TabsTrigger value="completed">
              Completed ({completedCount})
            </TabsTrigger>
            <TabsTrigger value="cancelled">
              Cancelled ({cancelledCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        {/* Request List */}
        <div className="space-y-3">
          {filteredRequests.length === 0 ? (
            <Card className="bg-slate-800/50">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
                  <Truck className="h-8 w-8 text-slate-500" />
                </div>
                <h3 className="font-medium mb-1">
                  {searchQuery ? 'No matching requests' : 'No requests yet'}
                </h3>
                <p className="text-sm text-slate-400 mb-4">
                  {searchQuery 
                    ? 'Try adjusting your search terms' 
                    : 'Your roadside assistance requests will appear here'
                  }
                </p>
                {!searchQuery && (
                  <Button onClick={() => navigate('/roadside')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Request Assistance
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredRequests.map((request) => (
              <RequestCard key={request.id} request={request} />
            ))
          )}
        </div>
        
        {/* Notification Preferences Section */}
        <div className="mt-8">
          <NotificationPreferences />
        </div>
      </div>
      
      {/* Review Dialog - Using hook props */}
      <ReviewDialog {...reviewDialogProps} />
    </div>
  );
}
